import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { DeliveryDialog } from '../../components/delivery-dialog';
import { InventoryTable } from '../../components/inventory-table';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { inventoryQuery } from '../../lib/queries';

export const Route = createFileRoute('/_auth/inventory')({
  component: InventoryPage,
});

function InventoryPage() {
  const { session } = Route.useRouteContext();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [deliveryItem, setDeliveryItem] = useState<{ id: string; name: string } | null>(null);

  const params = {
    ...(categoryFilter && { category: categoryFilter }),
    ...(lowStockOnly && { low_stock_only: true }),
  };

  const { data, isLoading } = useQuery(inventoryQuery(params));

  const items = data?.items ?? [];

  function handleLogDelivery(itemId: string) {
    const item = items.find((i) => i.item_id === itemId);
    if (item) setDeliveryItem({ id: item.item_id, name: item.name });
  }

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Feed Inventory</h1>
        {data && (
          <span className="text-sm text-ndd-400">
            Total Value:{' '}
            <span className="text-white font-semibold">
              ${data.total_value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </span>
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All categories</SelectItem>
            <SelectItem value="silage">Silage</SelectItem>
            <SelectItem value="haylage">Haylage</SelectItem>
            <SelectItem value="grain">Grain</SelectItem>
            <SelectItem value="supplement">Supplement</SelectItem>
            <SelectItem value="mineral">Mineral</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>

        <button
          type="button"
          onClick={() => setLowStockOnly((v) => !v)}
          className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
            lowStockOnly ? 'bg-red-600 text-white' : 'bg-ndd-900 text-ndd-300 hover:bg-ndd-800'
          }`}
        >
          Low Stock Only
        </button>

        {(categoryFilter || lowStockOnly) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCategoryFilter('');
              setLowStockOnly(false);
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-ndd-400">Loading…</div>
      ) : (
        <InventoryTable items={items} role={session.role} onLogDelivery={handleLogDelivery} />
      )}

      {deliveryItem && (
        <DeliveryDialog
          open={!!deliveryItem}
          onOpenChange={(open) => {
            if (!open) setDeliveryItem(null);
          }}
          itemId={deliveryItem.id}
          itemName={deliveryItem.name}
        />
      )}
    </div>
  );
}
